import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import {
  BaseAssignmentResponseDtoStatus,
  CreateHomeworkAssignmentRequestBodyGenerator,
} from "@packages/api/madlenAPI.schemas";
import { Button, Text } from "../../../core";
import { trackEvent } from "@packages/hooks/useAnalytics";
import {
  BookText,
  FileText,
  List,
  Youtube,
  GraduationCap,
  BookA,
  ClipboardPenLine,
} from "lucide-react";

interface GenerationOption {
  type: CreateHomeworkAssignmentRequestBodyGenerator;
  title: string;
  icon: React.ReactNode;
  borderColor: string;
  hoverColor: string;
  hoverTextColor: string;
}

const AllAssignmentsHeader = ({
  activeFilter,
  setActiveFilter,
}: {
  activeFilter: BaseAssignmentResponseDtoStatus | undefined;
  setActiveFilter: (
    filter: BaseAssignmentResponseDtoStatus | undefined,
  ) => void;
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { trackEvent: track } = trackEvent();
  const [hoveredOption, setHoveredOption] =
    useState<CreateHomeworkAssignmentRequestBodyGenerator | null>(null);

  const generationOptions: GenerationOption[] = [
    ...(i18n.language === "tr"
      ? [
          {
            type: "LEARNING_OUTCOME" as CreateHomeworkAssignmentRequestBodyGenerator,
            title: t("assignments.generator.learningOutcome.title"),
            icon: (
              <BookA className="w-8 h-8 text-blue-500 dark:text-blue-400" />
            ),
            borderColor: "border-blue-200 dark:border-blue-800/40",
            hoverColor:
              "bg-gradient-to-br from-blue-50 to-sky-50 dark:from-blue-900/20 dark:to-sky-900/20",
            hoverTextColor:
              "from-blue-600 via-sky-500 to-indigo-400 dark:from-blue-400 dark:via-sky-300 dark:to-indigo-200",
          },
        ]
      : []),
    {
      type: "PROMPT",
      title: t("assignments.generator.prompt.title"),
      icon: (
        <ClipboardPenLine className="w-8 h-8 text-purple-500 dark:text-purple-400" />
      ),
      borderColor: "border-purple-200 dark:border-purple-800/40",
      hoverColor:
        "bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20",
      hoverTextColor:
        "from-pink-600 via-purple-500 to-indigo-400 dark:from-pink-400 dark:via-purple-300 dark:to-indigo-200",
    },
    {
      type: "TOPIC_LIST",
      title: t("assignments.generator.topic.title"),
      icon: <List className="w-8 h-8 text-emerald-500 dark:text-emerald-400" />,
      borderColor: "border-emerald-200 dark:border-emerald-800/40",
      hoverColor:
        "bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20",
      hoverTextColor:
        "from-green-600 via-teal-500 to-blue-400 dark:from-green-400 dark:via-teal-300 dark:to-blue-200",
    },
    {
      type: "YOUTUBE_LINK",
      title: t("assignments.generator.youtube.title"),
      icon: <Youtube className="w-8 h-8 text-red-500 dark:text-red-400" />,
      borderColor: "border-red-200 dark:border-red-800/40",
      hoverColor:
        "bg-gradient-to-br from-red-50 to-pink-50 dark:from-red-900/20 dark:to-pink-900/20",
      hoverTextColor:
        "from-red-600 via-pink-500 to-orange-400 dark:from-red-400 dark:via-pink-300 dark:to-orange-200",
    },
    {
      type: "DRIVE_FILE",
      title: t("assignments.generator.file.title"),
      icon: (
        <FileText className="w-8 h-8 text-orange-500 dark:text-orange-400" />
      ),
      borderColor: "border-orange-200 dark:border-orange-800/40",
      hoverColor:
        "bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-900/20 dark:to-amber-900/20",
      hoverTextColor:
        "from-orange-600 via-amber-500 to-yellow-400 dark:from-orange-400 dark:via-amber-300 dark:to-yellow-200",
    },
  ];

  const handleGenerationClick = (option: GenerationOption) => {
    track(
      {
        category: "ContentGeneration",
        action: "assignment.generate",
        label: "Assignment generation click",
        nonInteraction: false,
      },
      {
        location: "/assignment/generate",
        element: "generate-button",
        assignmentGenerationType: option.type,
      },
    );
    navigate(`/assignments/generate/${option.type.toLowerCase()}`);
  };

  return (
    <>
      <div className="flex flex-col md:flex-row justify-between items-start mb-4 md:mb-8">
        <div className="">
          <Text variant="h1">assignments.title</Text>
          <Text variant="p" className="text-gray-600 dark:text-gray-400 mt-1">
            assignments.subtitle
          </Text>
        </div>
        <div className="grid grid-cols-2 sm:flex gap-2 mt-4 md:mt-0">
          <Button
            size="md"
            rounded="lg"
            bgColor={
              !activeFilter
                ? "bg-indigo-100 dark:bg-indigo-900"
                : "bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
            }
            textColor={
              !activeFilter
                ? "text-indigo-700 dark:text-indigo-100"
                : "text-gray-700 dark:text-gray-300"
            }
            className={`${
              !activeFilter
                ? "border-indigo-300 dark:border-indigo-700"
                : "border-gray-300 dark:border-gray-700"
            }`}
            onClick={() => setActiveFilter(undefined)}
          >
            {t("assignments.filters.all")}
          </Button>
          <Button
            size="md"
            rounded="lg"
            bgColor={
              activeFilter === "DRAFT"
                ? "bg-indigo-100 dark:bg-indigo-900"
                : "bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
            }
            textColor={
              activeFilter === "DRAFT"
                ? "text-indigo-700 dark:text-indigo-100"
                : "text-gray-700 dark:text-gray-300"
            }
            variant="outline"
            className={`${
              activeFilter === "DRAFT"
                ? "border-indigo-300 dark:border-indigo-700"
                : "border-gray-300 dark:border-gray-700"
            }`}
            onClick={() =>
              activeFilter !== "DRAFT"
                ? setActiveFilter("DRAFT")
                : setActiveFilter(undefined)
            }
          >
            {t("assignments.filters.draft")}
          </Button>
          <Button
            size="md"
            rounded="lg"
            bgColor={
              activeFilter === "ASSIGNED"
                ? "bg-indigo-100 dark:bg-indigo-900"
                : "bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
            }
            textColor={
              activeFilter === "ASSIGNED"
                ? "text-indigo-700 dark:text-indigo-100"
                : "text-gray-700 dark:text-gray-300"
            }
            variant="outline"
            className={`${
              activeFilter === "ASSIGNED"
                ? "border-indigo-300 dark:border-indigo-700"
                : "border-gray-300 dark:border-gray-700"
            }`}
            onClick={() =>
              activeFilter !== "ASSIGNED"
                ? setActiveFilter("ASSIGNED")
                : setActiveFilter(undefined)
            }
          >
            {t("assignments.filters.assigned")}
          </Button>
          {/* <Button
            size="md"
            rounded="lg"
            bgColor={
              activeFilter === "PENDING"
                ? "bg-indigo-100 dark:bg-indigo-900"
                : "bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
            }
            textColor={
              activeFilter === "PENDING"
                ? "text-indigo-700 dark:text-indigo-100"
                : "text-gray-700 dark:text-gray-300"
            }
            variant="outline"
            className={`${
              activeFilter === "PENDING"
                ? "border-indigo-300 dark:border-indigo-700"
                : "border-gray-300 dark:border-gray-700"
            }`}
            onClick={() =>
              activeFilter !== "PENDING"
                ? setActiveFilter("PENDING")
                : setActiveFilter(undefined)
            }
          >
            {t("assignments.filters.pending")}
          </Button> */}
        </div>
      </div>

      <div
        className={`grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mb-8 ${generationOptions.length === 5 ? "xl:grid-cols-5" : "xl:grid-cols-4"}`}
      >
        {generationOptions.map((option, index) => {
          const isHovered = hoveredOption === option.type;
          const isLastItemOnMobile = index === generationOptions.length - 1;

          return (
            <Button
              key={option.type}
              onClick={() => handleGenerationClick(option)}
              onMouseEnter={() => setHoveredOption(option.type)}
              onMouseLeave={() => setHoveredOption(null)}
              rounded="lg"
              bgColor={option.hoverColor}
              className={`${
                isLastItemOnMobile && generationOptions.length % 2 !== 0
                  ? "col-span-2 sm:col-span-2 md:col-span-1"
                  : ""
              } 
        group sm:rounded-xl relative overflow-hidden border-2 ${
          option.borderColor
        } shadow-sm hover:shadow-md transition-all duration-300 active:scale-95 ${
          isHovered ? `scale-[1.02] md:scale-105` : ""
        } h-full p-7`}
            >
              <div className="flex flex-col items-center justify-center h-full">
                <div className="flex items-center justify-center mb-2">
                  {option.icon}
                </div>
                <div
                  className={`text-base sm:leading-5 font-bold bg-gradient-to-r ${option.hoverTextColor} bg-clip-text text-transparent text-center`}
                >
                  {option.title}
                </div>
                <div
                  className={`absolute bottom-2 left-1/2 -translate-x-1/2 flex items-center text-sm font-semibold transition-all bg-gradient-to-r ${
                    option.hoverTextColor
                  } bg-clip-text text-transparent ${
                    isHovered
                      ? "opacity-100 translate-y-0"
                      : "opacity-0 translate-y-2"
                  }`}
                >
                  {t("common.getStarted")} →
                </div>
              </div>
            </Button>
          );
        })}
      </div>
    </>
  );
};

export default AllAssignmentsHeader;
